<template>
	<div>
		<nuxt-link v-bind="linkProps" class="list-item nav-list-item" role="listitem"
				   @click="handleClick"
				   :style="style"
				   :class="[{'list-item-parent': props.hasChildren, 'list-item--disabled': props.disabled}, props.level ? `list-item-level-${props.level}` : '', sizeableString ? 'nav-list-item-' + sizeableString : '', colorClass ? 'text-' + colorClass : '', props.customClass]">
			<span v-if="props.level >= 3 && isActive" class="active-border" :style="{backgroundColor: isActive ? activeColorHex : null}"/>

			<div class="flex justify-between items-center w-full h-full">
				{{ text }}
				<slot/>
				<div v-if="props.hasChildren || $slots.right" class="flex items-center justify-end h-full ml-4">
					<slot name="right"/>
					<Icon v-if="props.hasChildren" class="ml-2" :color="textColor ?? 'gray-400'" small force-size
						  :icon="isActive ? icons.up : icons.down"/>
				</div>
			</div>
		</nuxt-link>

		<expand-transition v-if="$slots.nested">
			<div v-show="isActive">
				<slot name="nested"/>
			</div>
		</expand-transition>
	</div>
</template>

<script setup lang="ts">
import {useRuntimeConfig} from '#app';
import {watch, ref, inject, computed} from 'vue';
import {mdiChevronDown, mdiChevronUp} from "@mdi/js";
import {convertHexToRGBA, getBestContrastHexColor} from "@spoferan/spoferan-ts-core";
import {type SizeableProps, useSizeable} from "../../../composables/useSizeable";
import {type ColorableProps, useColorable} from "../../../composables/useColorable";
import {type LinkableProps, useLinkable} from "../../../composables/useLinkable";
import {navListInjection} from "../../../types/keys";

const props = defineProps<{
	value?: string|number;

	text?: string|number;

	// The color to style items on active state
	activeColor?: string;

	hasChildren?: boolean;

	// The children level of the item
	// null: No styling
	// 1: Full background color on active state
	// 2: Light opacity background color on active state
	// 3: Border color on active state
	level?: number;

	active?: boolean,

	// Custom padding classes to apply
	customClass?: string,

} & LinkableProps & SizeableProps & ColorableProps>();

const emit = defineEmits(['click', 'update:active'])

const config = useRuntimeConfig();
const {sizeableString} = useSizeable(props);
const {colorClass, colorHex} = useColorable(props);
const {
	linkProps,
	hasActiveTo,
} = useLinkable(props);

const navList = inject(navListInjection);
const isActive = ref(props.active ?? false);

// Keep the active prop in sync with the active state
watch(isActive, (isActive) => {
	emit('update:active', isActive);
});

// Adapt the active state whenever the item became active due to route changes
watch(hasActiveTo, (active) => {
	if (!props.disabled) {
		setActiveState(active);
	}
}, { immediate: true });

// Adapt the active state whenever the active value of the parent nav list changes
watch(navList.values, (values) => {
	isActive.value = values.includes(props.value);
});

function setActiveState(active: boolean) {
	isActive.value = active;
	handleInternallyChangedActiveState(false);
}

function handleClick(event) {
	if (!props.disabled) {
		isActive.value = !isActive.value;
		emit('click', event);

		// When the user explicitly clicks the item, we always want to only have a single active value
		handleInternallyChangedActiveState(true);
	}
}

function handleInternallyChangedActiveState(forceSingleValue: boolean) {
	// We only emit the active change to the parent nav list if changed internally,
	// as the parent nav list will already receive the active change from the outside when changed outside

	if (!props.value) {
		return;
	}

	if (isActive.value) {
		if (forceSingleValue) {
			navList?.setValues([props.value]);
		} else {
			navList?.addValue(props.value);
		}
	} else {
		navList?.removeValue(props.value);
	}
}

const icons = {
	up: mdiChevronUp,
	down: mdiChevronDown,
}

const activeColorHex = computed(() => {
	return props.activeColor ?? (props.level ? config.public.colors.primary : null);
});
const textColor = computed(() => {
	return isActive.value && activeColorHex.value ? getBestContrastHexColor(activeColorHex.value) : colorHex.value;
});
const style = computed(() => {
	if (!props.level) {
		return {
			color: isActive.value ? activeColorHex.value : colorHex.value,
		};
	}

	switch (props.level) {
		case 1:
			return {
				backgroundColor: isActive.value ? activeColorHex.value : null,
				color: textColor.value,
				borderRadius: isActive.value && props.hasChildren ? '0 5px 0 0' : '0 5px 5px 0',
			}
		case 2:
			return {
				backgroundColor: isActive.value ? convertHexToRGBA(activeColorHex.value, 0.85) : null,
				color: textColor.value,
				borderLeft: `4px solid ${activeColorHex.value}`,
      }
		case 3:
			return {
				borderLeft: `4px solid ${activeColorHex.value}`,
			}
		case 4:
			return {
				borderLeft: `4px solid ${activeColorHex.value}`,
			}
	}

	return null;
});

</script>

<style>
/*********************
  Inputs
**********************/
/* ==========================================================================
   Mixins
   ========================================================================== */
.app .list-item.nav-list-item {
  color: #465050;
  align-items: center;
  display: flex;
  letter-spacing: normal;
  outline: none;
  padding: 12px 16px;
  position: relative;
  cursor: pointer;
  font-size: 15px;
}
.app .list-item.nav-list-item.rounded-md {
  border-radius: 32px !important;
}
.app .list-item.nav-list-item:before {
  background-color: currentColor;
  border-radius: inherit;
  bottom: 0;
  content: "";
  left: 0;
  opacity: 0;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;
}
.app .list-item.nav-list-item:hover:before {
  opacity: 0.04;
}
.app .list-item.nav-list-item.list-item--disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
.app .list-item.nav-list-item.list-item--disabled:hover:before {
  opacity: 0;
}
.app .list-item.nav-list-item.list-item--active:before {
  background-color: #7D8484;
  opacity: 0.12;
}
.app .list-item.nav-list-item.list-item--active .active-border {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  background-color: #008200;
}
.app .list-item.nav-list-item.nav-list-item-xs {
  font-size: 13px;
  padding: 8px 16px;
}
.app .list-item.nav-list-item.nav-list-item-sm {
  font-size: 14px;
  padding: 10px 16px;
}
.app .list-item.nav-list-item.nav-list-item-lg {
  padding: 14px 16px;
}
</style>